import {
    createContext,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import ConfirmModal from "../components/ConfirmModal";
import useTestData from "../hooks/useTestData";
import useAnalytics from "../hooks/useAnalytics";

export type ConfirmOptions = {
    title: string;
    description?: string;
    confirmText: string;
    cancelText: string;
    hideCancelButton?: boolean;
    onConfirm?: () => Promise<void> | void;
    onCancel?: () => Promise<void> | void;
    onClose?: () => Promise<void> | void;
};

export type Nullable<T> = T | null;

export type ConfirmContextType = {
    show: (options: Partial<ConfirmOptions>) => void;
};

export const ConfirmContext = createContext<Nullable<ConfirmContextType>>(null);

type Props = {
    children: ReactNode;
};

const ConfirmContextProvider = ({ children }: Props) => {
    const { value: testData } = useTestData();
    const { updateAnalytics } = useAnalytics();
    const reportExists = testData?.navbarStatus && testData?.navbarStatus >= 2;
    const initialConfirm: ConfirmOptions = useMemo(
        () => ({
            title: "Are you sure you want to confirm?",
            description: reportExists
                ? "Confirming will override the existing report."
                : undefined,
            confirmText: "Confirm",
            cancelText: "Cancel",
        }),
        [reportExists]
    );
    const [confirm, setConfirm] = useState<ConfirmOptions>(initialConfirm);

    useEffect(() => {
        setConfirm((prev) => ({
            ...prev,
            description: reportExists
                ? "Confirming will override the existing report."
                : undefined,
        }));
    }, [reportExists]);

    const [isOpen, setIsOpen] = useState(false);

    const onCloseHandler = () => {
        setIsOpen(false);
        confirm.onClose?.();
    };

    const show = useCallback<ConfirmContextType["show"]>(
        (confirmOptions) => {
            setConfirm({ ...initialConfirm, ...confirmOptions });
            setIsOpen(true);
        },
        [setIsOpen, initialConfirm]
    );

    const onConfirmHandler = async () => {
        await confirm?.onConfirm?.();
        if (reportExists && testData) {
            // Clear scroll position when report is overridden
            updateAnalytics({ scroll_position: 0 });
        }
        setIsOpen(false);
    };

    const onCancelHandler = async () => {
        await confirm?.onCancel?.();
        setIsOpen(false);
        // Reset confirm options to initial state
        setTimeout(() => {
            setConfirm(initialConfirm);
        }, 300);
    };

    const value = useMemo(() => ({ show }), [show]);

    return (
        <ConfirmContext.Provider value={value}>
            <ConfirmModal
                title={confirm.title}
                description={confirm.description}
                confirmText={confirm.confirmText}
                cancelText={confirm.cancelText}
                hideCancelButton={confirm.hideCancelButton}
                onCancel={onCancelHandler}
                onConfirm={onConfirmHandler}
                onClose={onCloseHandler}
                isOpen={isOpen}
            />
            {children}
        </ConfirmContext.Provider>
    );
};

export default ConfirmContextProvider;
